import { Box } from "@mui/material"
import type { ReactElement } from "react"
import { useState } from "react"
import type { FieldError } from "react-hook-form"

import { FormSectionLabel } from "../FormSectionLabel"

import { WarehouseLocationCard } from "./WarehouseLocationCard"

import { AerosAutocomplete } from "@/components/UiKit"
import type { Option } from "@/types/form-fields"

interface ShipFromSelectorSingleProps {
  mode: "single"
  productId: number
  shipFromOptions: Option<string>[]
  selectedShipFromId: string
  error?: FieldError
  onAdd: (warehouseId: string) => void
  onRemove: () => void
}

interface ShipFromSelectorMultiProps {
  mode: "multi"
  productId: number
  shipFromOptions: Option<string>[]
  selectedShipFromIds: string[]
  error?: FieldError
  onAdd: (warehouseId: string) => void
  onRemove: (warehouseId: string) => void
}

type ShipFromSelectorProps = ShipFromSelectorSingleProps | ShipFromSelectorMultiProps

export const ShipFromSelector = (props: ShipFromSelectorProps): ReactElement => {
  const { mode, productId, shipFromOptions, error } = props
  const [filter, setFilter] = useState("")

  const selectedIds =
    mode === "single" ? (props.selectedShipFromId ? [props.selectedShipFromId] : []) : props.selectedShipFromIds
  const unselectedOptions = shipFromOptions.filter((option) => !selectedIds.includes(option.value))
  const selectedOptions = shipFromOptions.filter((option) => selectedIds.includes(option.value))

  const handleAdd = (warehouseId: string) => {
    props.onAdd(warehouseId)
    setFilter("")
  }

  const handleRemove = (warehouseId: string) => {
    if (mode === "single") {
      props.onRemove()
    } else {
      props.onRemove(warehouseId)
    }
  }

  const showAutocomplete = mode === "single" ? selectedIds.length === 0 : unselectedOptions.length > 0

  return (
    <Box className='flex flex-col gap-2'>
      <FormSectionLabel label='Ship From' />
      {showAutocomplete && (
        <AerosAutocomplete
          options={unselectedOptions}
          value={null}
          inputValue={filter}
          onInputChange={setFilter}
          onChange={(warehouseId) => {
            if (warehouseId) handleAdd(warehouseId)
          }}
          forcePopupIcon={false}
          required
          placeholder='Ship From Location'
          error={Boolean(error)}
          helperText={error?.message}
          data-testid={`sales.product-pricing-form.ship-from-field-${productId}`}
        />
      )}
      {selectedOptions.length > 0 && (
        <Box className='flex flex-col gap-2'>
          {selectedOptions.map((option) => (
            <WarehouseLocationCard
              key={option.value}
              warehouseId={option.value}
              label={option.label}
              onRemove={handleRemove}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
